/**
 * Integrytas Group — DDM (Demo Data Mirror)
 * DdmModeToggle — header icon button placed beside the theme/language
 * switchers. Flips the session between production and demo data (the
 * `demo` schema, a full replica of `public` — see supabase/migrations/
 * 20260719000000_ddm_module.sql). Every subsequent gateway call carries the
 * choice via x-ddm-schema (api_client.ts); DdmDemoBanner reflects it.
 *
 * Switching goes through switchDdmSchema(), which re-mints the session and
 * reloads — a bare schema flip would leave the app showing one side's
 * permissions over the other side's data.
 */

import { useState } from 'react';
import { Database } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDdmSchema, switchDdmSchema } from '../../lib/ddm_mode';

export function DdmModeToggle() {
  const { t } = useTranslation();
  const schema = useDdmSchema();
  const [switching, setSwitching] = useState(false);
  const isDemo = schema === 'demo';

  // switchDdmSchema reloads the page, so this only ever resolves on failure —
  // it exists to stop a second click landing while the first is in flight.
  const onSwitch = () => {
    if (switching) return;
    setSwitching(true);
    void switchDdmSchema(isDemo ? 'public' : 'demo').finally(() => setSwitching(false));
  };

  return (
    <button
      type="button"
      onClick={onSwitch}
      disabled={switching}
      aria-label={isDemo
        ? t('ddm.toggle_to_production', 'Switch to production data')
        : t('ddm.toggle_to_demo', 'Switch to demo data')}
      title={isDemo
        ? t('ddm.toggle_to_production', 'Switch to production data')
        : t('ddm.toggle_to_demo', 'Switch to demo data')}
      className={
        isDemo
          ? 'flex h-7 items-center gap-1 rounded border border-white/40 bg-white/20 px-1.5 text-white'
          : 'flex h-7 w-7 items-center justify-center rounded text-white/80 hover:bg-white/10 hover:text-white'
      }
    >
      <Database className="h-4 w-4" />
      {isDemo && <span className="text-csm-2xs font-semibold uppercase tracking-wide">{t('ddm.badge_demo', 'Demo')}</span>}
    </button>
  );
}
